import { useCallback, useContext, useEffect, useRef } from "react";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { Message, MessageType } from "@app/notifications";
import { ProjectRunStatusChangePayload } from "@app/notifications/message";
import { NotificationContext } from "@app/notifications/NotificationContext";
import {NetworkPortfolioManagementProject, SortByOption} from "@app/network-portfolio-management/interfaces";
import { NPM_QUERY_KEYS } from "@app/network-portfolio-management/npmQueryKeys";
import NetworkPortfolioManagementService from "@app/network-portfolio-management/services/NetworkPortfolioManagementService";

const DEBOUNCE_MS = 400;

// Single-project run/status events the Classic dashboard reacts to in its project tiles/table
// (home/ProjectTile.tsx, home/ProjectTable.tsx). The "Project Actions -> Run Projects" fly-out
// emits ProjectRunStatusChange; the processing events cover the intermediate status transitions.
// All share the same single-projectId payload (ProjectRunStatusChangePayload).
const STATUS_CHANGE_MESSAGE_TYPES: MessageType[] = [
    MessageType.ProjectRunStatusChange,
    MessageType.ProcessingProjectionAnalysis,
    MessageType.ProcessingSpecialtyGroupExceptions,
    MessageType.ProcessingProviderChanges,
    MessageType.ProcessingProspectMarketNcp,
];

export function useProjectStatusNotification(
    projectIds: number[],
    service: NetworkPortfolioManagementService,
    sortBy: SortByOption,
): void {
    const notificationContext = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingIdsRef = useRef<Set<number>>(new Set());
    // Holds the AbortController for the in-flight flush fetch so it can be cancelled on unmount.
    const abortControllerRef = useRef<AbortController | null>(null);
    // Guards cache writes against an unmount that lands while a flush fetch is in flight.
    const mountedRef = useRef(true);
    // Ref keeps the latest projectIds accessible inside the stable handleNotification closure
    // without requiring the effect to re-subscribe on every projectIds change.
    const projectIdsRef = useRef(projectIds);

    useEffect(() => {
        projectIdsRef.current = projectIds;
    }, [projectIds]);

    const handleNotification = useCallback((message: Message): void => {
        if (!STATUS_CHANGE_MESSAGE_TYPES.includes(message.notificationType)) return;

        const { projectId } = message.payload as ProjectRunStatusChangePayload;
        if (!projectIdsRef.current.includes(projectId)) return;

        pendingIdsRef.current.add(projectId);

        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(async () => {
            const affectedIds = [...pendingIdsRef.current];
            pendingIdsRef.current.clear();
            debounceTimerRef.current = null;

            const matchingQueries = (query: { queryKey: readonly unknown[] }): boolean => {
                const [matchingQueryKey, keyProjectIds] = query.queryKey as [string, number[], ...unknown[]];
                return (
                    matchingQueryKey === NPM_QUERY_KEYS.projects
                    && Array.isArray(keyProjectIds)
                    && affectedIds.some((id) => (keyProjectIds as number[]).includes(id))
                );
            };

            // Only fetch projects actually present in a loaded page — that is what the user sees.
            // Affected projects beyond the loaded pages are refetched by the normal queryFn when
            // scrolled into view, so fetching them here would be discarded by the patch below.
            const loadedIds = new Set<number>();
            queryClient
                .getQueriesData<InfiniteData<NetworkPortfolioManagementProject[]>>({ predicate: matchingQueries })
                .forEach(([, data]) => data?.pages.forEach((page) => page.forEach((p) => loadedIds.add(p.projectId))));
            const idsToFetch = affectedIds.filter((id) => loadedIds.has(id));

            if (idsToFetch.length === 0) return;

            // Single controller per flush; we intentionally do NOT abort a prior in-flight fetch when
            // a new flush starts — each flush carries its own accumulated IDs and aborting would drop
            // a valid update. Only unmount aborts.
            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            try {
                const freshProjects = await service.getProjectsData({
                    pageIndex: 1,
                    pageSize: idsToFetch.length,
                    projectIds: idsToFetch,
                    sortBy: sortBy,
                }, abortController.signal);

                if (!mountedRef.current) return;

                const freshMap = new Map(freshProjects.map((p) => [p.projectId, p]));

                queryClient.setQueriesData<InfiniteData<NetworkPortfolioManagementProject[]>>(
                    { predicate: matchingQueries },
                    (oldData) => {
                        if (!oldData) return oldData;
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page) =>
                                page.map((project) =>
                                    freshMap.has(project.projectId)
                                        ? (freshMap.get(project.projectId) as NetworkPortfolioManagementProject)
                                        : project,
                                ),
                            ),
                        };
                    },
                );
            } catch (_error) {
                if (!mountedRef.current) return;
                queryClient.invalidateQueries({ predicate: matchingQueries });
            } finally {
                if (abortControllerRef.current === abortController) {
                    abortControllerRef.current = null;
                }
            }
        }, DEBOUNCE_MS);
    }, [queryClient, service]);

    useEffect(() => {
        mountedRef.current = true;
        notificationContext.subscribe(handleNotification);
        return () => {
            mountedRef.current = false;
            notificationContext.unsubscribe(handleNotification);
            if (debounceTimerRef.current !== null) {
                clearTimeout(debounceTimerRef.current);
            }
            if (abortControllerRef.current !== null) {
                abortControllerRef.current.abort();
            }
        };
    }, [handleNotification, notificationContext]);
}
